import { Injectable } from '@angular/core';
import { Subject, defer, of } from 'rxjs';
import { filter, delay, takeUntil, catchError, retryWhen } from 'rxjs/operators';
import { NotificationsService, createNotify, Subscription, Config } from '@mitel/cloudlink-sdk';
import { v4 as uuidv4 } from 'uuid';
import { RetryUtil } from '../utils/retry.util';

declare const Paho: any;

const logContext = "CloudLinkNotifyService";

@Injectable({
    providedIn: 'root'
})

export class CloudLinkNotifyService {

    private notify: NotificationsService = null;
    private subscriptions: Subscription[] = [];
    private subRefreshes = [];
    private workflowSubscription: Subscription = null;
    private billingAsyncSubscription: Subscription = null;
    private listenSubscription = null;

    eventNotification$: Subject<any> = new Subject();

    private deviceId = 'cl-account-app/' + uuidv4();
    private workflowId: string;

    constructor () {}

    initializeNotify()
    {
        this.createCloudLinkNotify();
        // we will listen once for now
        // new validator WFs are coming and this might change how we are handling this
        if ( this.notify && !this.listenSubscription )
          this.listenForNotificationChanges();
    }

    async initializeNotifyForBillingAsync()
    {
        console.log(`${logContext}.initializeNotifyForBillingAsync start`);
        if ( this.billingAsyncSubscription )
            return; // not required
        await this.subscribeToBillingAsyncJobs();
    }

    async initializeNotifyForWorkflow( activityId: string )
    {
        console.log(`${logContext}.initializeNotifyForWorkflow for activityId ${activityId}`);
        if ( this.workflowSubscription )
            return; // request in progress
        await this.subscribeToWorkflow(activityId);
    }

    private createCloudLinkNotify() {
      if (!this.notify) {
        try {
          this.notify = createNotify({
            websocket: {
              deviceId: this.deviceId,
              pahoMqtt: (websocketUrl, clientId) => {
                const client = new Paho.Client(websocketUrl, clientId);
                return client;
              },
              autoReconnect: true
            }
          });
          Config.notifications = this.notify;
          console.log('[cloudlink-notify.service.createNotify()]');
        } catch (error) {
          console.error('[cloudlink-notify.service.createNotify()]', error);
        }
      }
    }

    private async subscribeToWorkflow( activityId: string, retry = true): Promise<void> {
        console.log(`${logContext}.subscribeToWorkflow for activityId ${activityId}`);
    
        await this.notify.subscribe({
            transport: 'websocket',
            topic: 'platform-api-workflow',
            subjectFilter: `/activities/${activityId}/workers`
          }).then((subscription: Subscription) => {
            console.log(`${logContext}.subscribeToWorkflow SUBSCRIBE subscriptionId=`, { subscriptionId: subscription.subscriptionId, expiryDateTime: subscription.expiryDateTime, workflowId: activityId });
            this.workflowSubscription = subscription;
	          this.workflowId = activityId;
          })
        .catch(error => {
            console.error(`${logContext}.subscribeToWorkflow for activityId ${activityId}, error`, error);
            if (retry) {
                setTimeout(() => {
                this.subscribeToWorkflow(activityId, false);
                }, 2000);
            } else {
                console.error(`${logContext}.subscribeToWorkflow for activityId ${activityId}, - could not reconnect`, error);
            }
        });
    }

    private async subscribeToBillingAsyncJobs(retry = true): Promise<void> {
      console.log(`${logContext}.subscribeToBillingAsyncJobs`);
  
      await this.notify.subscribe({
          transport: 'websocket',
          topic: 'platform-api-billing',
          subjectFilter: '/async-jobs'
        }).then((subscription: Subscription) => {
          console.log(`${logContext}.subscribeToBillingAsyncJobs SUBSCRIBE subscriptionId=`, { subscriptionId: subscription.subscriptionId, expiryDateTime: subscription.expiryDateTime });
          this.billingAsyncSubscription = subscription;
        })
      .catch(error => {
          console.error(`${logContext}.subscribeToBillingAsyncJobs, error`, error);
          if (retry) {
              setTimeout(() => {
              this.subscribeToBillingAsyncJobs(false);
              }, 2000);
          } else {
              console.error(`${logContext}.subscribeToBillingAsyncJobs - could not reconnect`, error);
          }
      });
    }

    private listenForNotificationChanges(): void {
        console.log('[cloudlink-notify.service.listenToWorkflow()]');

        this.listenSubscription = this.notify
            .asMessageObservable().pipe(
                filter(message => {
                  console.log('[cloudlink-notify.service.listenToWorkflow()]: got RAW notification:', message);
                  return (message.topic === 'platform-api-workflow' && message.method === 'DELETE') || 
                  (message.topic === 'platform-api-billing' && message.subject.indexOf('async-jobs') >= 0);
                })
            ).subscribe((message: any) => {
              console.log('[cloudlink-notify.service.listenToWorkflow()]: got FILTERED notification:', message);
              this.eventNotification$.next(message);
        });
    }

    async unsubscribeFromNotifications() {
      await this.unsubscribeFromWorkflow();
      await this.unsubscribeFromBillingAsync();
    }

    async unsubscribeFromBillingAsync() {
      if ( this.billingAsyncSubscription ) {
          await this.notify.unsubscribe({ subscriptionId: this.billingAsyncSubscription.subscriptionId });
          this.billingAsyncSubscription = null;
      }
    }

    async unsubscribeFromWorkflow() {
        if ( this.workflowSubscription ) {
            await this.notify.unsubscribe({ subscriptionId: this.workflowSubscription.subscriptionId });
            this.workflowSubscription = null;
	          this.workflowId = undefined;
        }
    }
}